import {
  Box,
  Button,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CartItem, InputCartTableProps } from "./interface";
import TableListTitle from "../TableList/TableListTitle";
import NumberFormat from "../NumberFormat";
import CardFormAlt from "../CardFormAlt";
import update from "immutability-helper";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  Row,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<CartItem>();

const InputCartTable: React.FC<InputCartTableProps> = ({ value, onChange }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  useEffect(() => {
    setItems(value || []);
  }, [value]);

  const addItem = () => {
    const newItems = update(items, {
      $push: [
        {
          itemName: "",
          qty: 1,
          price: 0,
        },
      ],
    });

    setItems(newItems);
    onChange(newItems);
  };

  const { confirmation, close } = useConfirmation();

  const onInputChange = useCallback(
    (index: number, itemName: string, price: number, qty: number) => {
      const newItems = update(items, {
        [index]: {
          itemName: {
            $set: itemName,
          },
          price: {
            $set: price,
          },
          qty: {
            $set: qty,
          },
        },
      });
      setItems(newItems);
      onChange(newItems);
    },
    [items, onChange]
  );

  const columns = useMemo(() => {
    const deleteRow = (index: number) => {
      confirmation({
        description: "Kamu yakin ingin menghapus data?",
        callback: async () => {
          const newItems = update(items, {
            $splice: [[index, 1]],
          });
          setItems(newItems);
          onChange(newItems);
          close();
        },
      });
    };

    return [
      columnHelper.accessor("itemName", {
        id: "itemName",
        header: () => "Item Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("qty", {
        id: "qty",
        header: () => "Qty",
      }),
      columnHelper.accessor("price", {
        id: "price",
        header: () => "Price",
      }),
      columnHelper.display({
        id: "subtotal",
        header: "Subtotal",
      }),
      columnHelper.display({
        id: "action",
        header: "",
        cell: ({ row }) => (
          <IconButton
            aria-label="Hapus"
            variant="icon"
            size="sm"
            icon={<DeleteIcon />}
            onClick={() => deleteRow(row.index)}
          />
        ),
      }),
    ];
  }, [close, confirmation, items, onChange]);

  const table = useReactTable({
    data: items,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowCanExpand: () => true,
  });

  return (
    <>
      <CardFormAlt>
        <TableListTitle
          title="Item"
          actionComponents={
            <Button size="sm" onClick={() => addItem()}>
              Add Item
            </Button>
          }
        />

        <Table>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <RowRender
                key={row.id}
                row={row}
                onChange={(name, price, qty) =>
                  onInputChange(row.index, name, price, qty)
                }
              />
            ))}
          </Tbody>
        </Table>

        {items.length <= 0 && (
          <Box textAlign="center" mt={2}>
            Items empty
          </Box>
        )}
      </CardFormAlt>
    </>
  );
};

const RowRender = ({
  row,
  onChange,
}: {
  row: Row<CartItem>;
  onChange: (name: string, price: number, qty: number) => void;
}) => {
  const [itemName, setItemName] = useState<string>(row.getValue("itemName"));
  const [price, setPrice] = useState<string>(row.getValue("price"));
  const [qty, setQty] = useState<string>(row.getValue("qty"));
  const total = useMemo(() => {
    return parseFloat(price) * parseInt(qty);
  }, [price, qty]);

  return (
    <Tr>
      {row.getVisibleCells().map((cell) => {
        return (
          <Td key={cell.id}>
            {cell.column.columnDef.id === "itemName" ? (
              <Textarea
              variant="outline"
                resize="none"
                size="sm"
                onChange={(e: any) => {
                  setItemName(e.target.value);
                  onChange(e.target.value, parseFloat(price), parseInt(qty));
                }}
                value={itemName.toString()}
              />
            ) : cell.column.columnDef.id === "price" ? (
              <Input
                size="sm"
                onChange={(e: any) => {
                  setPrice(e.target.value);
                  onChange(itemName, parseFloat(e.target.value), parseInt(qty));
                }}
                value={price.toString()}
                w="100px"
              />
            ) : cell.column.columnDef.id === "qty" ? (
              <NumberInput
                size="sm"
                w="70px"
                min={1}
                onChange={(v) => {
                  setQty(v);
                  onChange(itemName, parseFloat(price), parseInt(v));
                }}
                value={qty}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper
                    children={<ChevronUpIcon boxSize="14px" />}
                  />
                  <NumberDecrementStepper
                    children={<ChevronDownIcon boxSize="14px" />}
                  />
                </NumberInputStepper>
              </NumberInput>
            ) : cell.column.columnDef.id === "subtotal" ? (
              <Box whiteSpace="nowrap">
                <NumberFormat prefix="$ " value={total} />
              </Box>
            ) : (
              <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
            )}
          </Td>
        );
      })}
    </Tr>
  );
};

export default InputCartTable;
